import React, { PureComponent } from "react"
import PropTypes from "prop-types"
import Radium from "higher-order-components/configured-radium/"
import { HAS_TOUCH } from "constants/environment"
import { DetectCompact } from "higher-order-components"
import { Slider } from "containers"
import { LoaderIcon, Slideshow, CheckBox, WysiwygContent } from "components"
import bowser from "bowser"
import StockTile from "./stock-tile"
import SortMapping from "./sort-mapping"
import { H2, H4, H6, Section, Tag, Space } from "AtomicComponents"

const SortSymbol = ({ down }) => {
  const y1 = down ? 8 : 0
  const y2 = down ? 0 : 8
  return (
    <svg width="17" height="10" viewBox="0 0 17 10">
      <g fill="#999" fillRule="evenodd">
        <rect y={y1} width="17" height="2" rx="1" />
        <rect x="2" y="4" width="13" height="2" rx="1" />
        <rect x="5" y={y2} width="7" height="2" rx="1" />
      </g>
    </svg>
  )
}

SortSymbol.propTypes = {
  down: PropTypes.bool
}

const filterMapping = ({
  label, count, color, onClick, checked, key, ...extra
}, index) => {

  return (
    <CheckBox
      data-test={"filter"}
      key={key}
      label={<Tag checked={checked} color={checked ? color : "grey"}>{label}</Tag>}
      style={{
        marginRight: "0.2em"
      }}
      showBox={false}
      checked={checked}
      onClick={() => onClick({ key, checked: !checked })}
      {...extra} />
  )
}

filterMapping.propTypes = {
  count: PropTypes.number,
  label: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.onClick,
  checked: PropTypes.bool,
  key: PropTypes.string
}

const filterSelect = filters => ({ key }) => filters.some(filter => key === filter)

const ResponsiveStockTileLoop = Radium(DetectCompact({ threshold: [750, 1151] })((props) => {
  const { compact, dealerState } = props
  if (compact[0]) {
    return (
      <div
        style={{
          padding: "0 5vw",
          margin: "0 -0.5em"
        }}>
        <Slideshow showBullets bulletsBelow isItemWrapperStyle={false}>
          {props.stockList.map((props, index) => (
            <div
              style={{
                margin: "0.8em"
              }}
              key={index}>
              <StockTile dealerState={dealerState} {...props} />
            </div>
          ))}
        </Slideshow>
      </div>
    )
  } else if (compact[1]) {
    return (
      <Slider>
        {props.stockList.map((props, index) => (
          <div
            style={{
              width: "calc(45vw - 1.6em)",
              margin: "0.8em"
            }}
            key={index}>
            <StockTile dealerState={dealerState} {...props} />
          </div>
        ))}
      </Slider>
    )
  }
  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 5vw"
      }}>
      <StockTileLoop wrap {...props} compact={false} />
    </div>
  )
}))

class stockTileLoop extends PureComponent {
  constructor() {
    super()
    this.state = {
      hoverIndex: -1
    }
    this.nextUpdate = false
    this.setActiveIndex = this.setActiveIndex.bind(this)
  }
  setActiveIndex(index) {
    if (!this.props.focusActiveOnHover) return
    if (HAS_TOUCH) return
    if (this.nextUpdate) clearTimeout(this.nextUpdate)
    this.nextUpdate = setTimeout(() => {
      this.setState({ hoverIndex: index })
    }, 50)
  }

  render() {
    const {
      stockList, wrap, dealerState, compact
    } = this.props
    const { hoverIndex } = this.state
    const ghostLi = {
      minWidth: "20em",
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: "calc(33% - 1.6em)"
    }
    return (
      <ol
        style={{
          display: "flex",
          flexWrap: wrap ? "wrap" : "nowrap",
          justifyContent: "space-between",
          listStyleType: "none",
          padding: 0,
          margin: compact ? 0 : "0 -0.5em"
        }}>
        {stockList.map((props, index) => (
          <li
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: "calc(33% - 1.6em)",
              minWidth: "20em",
              margin: 0,
              padding: "0.8em",
              boxSizing: "border-box",
              display: bowser.safari && "flex"
            }}
            key={index}>
            <StockTile
              key={index}
              {...props}
              dealerState={dealerState} />
          </li>
        ))}
        {!compact && wrap && <li style={ghostLi} />}
        {!compact && wrap && <li style={ghostLi} />}
        {!compact && wrap && <li style={ghostLi} />}
      </ol>
    )
  }
}

stockTileLoop.defaultProps = {
  focusActiveOnHover: true,
  wrap: true
}

stockTileLoop.propTypes = {
  focusActiveOnHover: PropTypes.bool,
  stockList: PropTypes.array,
  wrap: PropTypes.bool
}

const StockTileLoop = Radium(stockTileLoop)

const ShowResults = ({
  dealerState,
  heading = "",
  pageDisclaimer = "",
  priceDisclaimer = "",
  preHeading = "",
  description = "",
  compact = true,
  filters = [],
  showLoader = false,
  components = [],
  stockList = [],
  featuredStockList = {},
  vehicleNotAvailableError,
  nameplate,
  sortOptions,
  handleSortChange,
  noResultsFoundMessage
}) => (
  <div style={{ position: "relative" }}>
    <Section padding>
      <H6 withDetails>{preHeading}</H6>
      <H2 withDetails>{heading}</H2>
      <Space />
      {description && (
        <WysiwygContent style={{ maxWidth: "45em", margin: "0 auto" }} xmlString={description} />
      )}
      {pageDisclaimer && (
        <WysiwygContent
          style={{ maxWidth: "30em", margin: "1em auto 0" }}
          textStyle={{ fontWeight: "600" }}
          xmlString={pageDisclaimer} />
      )}
      {priceDisclaimer && (
        <div
          style={{
            maxWidth: "30em",
            margin: "0 auto",
            color: "#999999",
            fontSize: "90%",
            lineHeight: "1.25em",
            fontWeight: 600
          }}>
          {priceDisclaimer.replace(/#STATE#/gi, dealerState)}
        </div>
      )}
      {vehicleNotAvailableError ? (
        <p style={{ color: "red", lineHeight: "1.5em", marginTop: "2em" }}>
          Sorry, the vehicle you were looking for is no longer available.
          <br />
          Take a look at other {nameplate} models available below.
        </p>
      ) : null}
    </Section>
    {showLoader ? (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "40vh" }}>
        <LoaderIcon style={{ margin: "initial" }} />
      </div>
    ) : (
      <div>
        {featuredStockList &&
          featuredStockList.stockList.length > 0 && (
            <div
              data-test="featured-block"
              style={{
                backgroundColor: "#fff",
                textAlign: "center",
                padding: compact ? "2em 0 1em" : "3em 0 4em",
                marginTop: "3em"
              }}>
              <div style={{ margin: "0 auto", maxWidth: "1200px", padding: "0 5vw" }} />
              <ResponsiveStockTileLoop
                stockList={featuredStockList.stockList}
                dealerState={dealerState} />
            </div>
          )}
        <Section horizontalPadding={!compact}>
          <div data-test="stock-filter" style={{ margin: "0 0 4em 0", width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: compact ? "column" : "row"
              }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: compact ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                <strong style={[styles.text, styles.greyText]}>SHOWING:</strong>
                <div>{filters.filter(filterSelect(["RUNOUT", "DEMO"])).map(filterMapping)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <strong style={[styles.text, styles.greyText]}>SORT RESULTS:</strong>
                <SortMapping sortOptions={sortOptions} handleSortChange={handleSortChange} />
              </div>
            </div>
            <div style={{ paddingTop: "0.5em" }}>
              {stockList && stockList.length > 0 ? (
                <StockTileLoop
                  compact={compact}
                  stockList={stockList}
                  dealerState={dealerState} />
              ) : (
                <H4>{noResultsFoundMessage || "No results to show"}</H4>
              )}
            </div>
          </div>
        </Section>
      </div>
    )}
  </div>
)

ShowResults.propTypes = {
  heading: PropTypes.string,
  pageDisclaimer: PropTypes.string,
  preHeading: PropTypes.string,
  description: PropTypes.string,
  filters: PropTypes.array,
  compact: PropTypes.bool,
  showLoader: PropTypes.bool,
  components: PropTypes.array,
  stockList: PropTypes.array,
  featuredStockList: PropTypes.object,
  vehicleNotAvailableError: PropTypes.number,
  nameplate: PropTypes.string,
  sortOptions: PropTypes.array,
  handleSortChange: PropTypes.func,
  noResultsFoundMessage: PropTypes.string
}

export default Radium(ShowResults)

const styles = {
  crossStyle: {
    fill: "currentColor",
    width: "10px",
    height: "10px"
  },
  ghostLi: { flex: "1 1 33%" },
  text: {
    textTransform: "uppercase",
    color: "#000",
    letterSpacing: "0.15em",
    fontSize: "90%"
  },
  greyText: {
    color: "#999",
    marginRight: "1em",
    fontSize: "81%"
  }
}



// WEBPACK FOOTER //
// ./src/containers/outlet-store/show-results.jsx